// Practice Setup v2 — step/group config, DQ registry, module display names.
// API steps ship live; Full-only steps render DQ-marked "Needs API" bodies.

export const MODULE_NAMES = {
  "edi-837-professional": "Professional claims (837P)",
  "edi-837-institutional": "Institutional claims (837I)",
  "edi-835-era": "Remittances (835)",
  "edi-278-preauth": "Prior authorization (278)",
  "edi-275-attachments": "Claim attachments (275)",
  "claim-scrubber": "Claim scrubber",
  "edi-837-workbench": "Claim workbench",
  "claim-search": "Claim search",
  "superbill-configurator": "Superbill configurator",
  "note-to-claim": "Note to claim",
  "superbill-execution": "Superbill processing",
  "superbill-patient-history": "Superbill patient history",
  "eob-to-era-execution": "EOB to ERA",
  "patient-statements": "Patient statements",
};
export const HIDDEN_MODULES = new Set(["revq-shawl"]);

// DQ registry (titles from the build brief §10). Used by the DQ drawer + markers.
export const DQ = {
  2: "Billing-rules PATCH: omit / [] semantics (full-replace confirmed)",
  3: "Billing rules: no version / audit trail for safe concurrent saves",
  4: "Client-level rules are write-only; precedence with provider rules",
  5: "Applied flag — when do rules start affecting AI coding?",
  6: "Rule vs NCCI edits conflict policy (e.g. blanket modifier 59)",
  7: "SOAP sample: accepted file types & max size",
  8: "CLOSED — a Default delete does not cascade to tov_formats slots",
  9: "Profiler leaks patient content (plan_subsections, abbreviations, excerpt)",
  10: "SOAP sample retention / downloadable original",
  11: "Does /dictation/soap-note read the provider's soap_profile?",
  12: "Note reprocess / stored format version",
  13: "Preview request body + 'Pull from past visit' route",
  14: "Exam PATCH partial-update behaviour (whole ros/pe replace?)",
  15: "'Modify with AI' route & propose-vs-write behaviour",
  16: "'Load defaults' source of baselines",
  17: "Attestation integrity (clear on change, physician identity)",
  18: "Restored attested_at write-back / restore event logging",
  19: "OpenAPI spec / authoritative route list",
  20: "Attestation legal authority (suggest: Review & authorize signature)",
  21: "Practice profile & Tax ID — no endpoint",
  22: "Locations & place of service — no endpoint",
  23: "Clearinghouse · EDI setup — no endpoint",
  24: "EHR/PM connection setup — no endpoint",
  25: "Payer enrollment — no endpoint",
  26: "Fee schedules — no endpoint",
  27: "Claim history import — no endpoint",
  28: "Patient balances / A·R — no endpoint",
  29: "Medical records access — no endpoint",
  30: "Practice preferences — no endpoint",
  31: "Authorizations & adjustments — no endpoint",
  32: "Modifier permissions — no endpoint",
  33: "Patient statements & self-pay — no endpoint",
  34: "Review & authorize — no endpoint",
  37: "Sub-logins / team management — no endpoint",
  38: "Provider ↔ client link history — no endpoint",
  40: "Template library (multiple named templates) — no endpoint",
  41: "Structured rules (id, target CPT/payer/modifier, enabled) — no endpoint",
  42: "Add-taxonomy-code route — undocumented",
  43: "Migrated-data totals (CPT/ICD/locations/modifiers) — no read",
  44: "Account type — no endpoint",
  45: "Subscription & billing — no endpoint",
  46: "More provider identifiers (CAQH, PTAN, Medicaid) — no read",
  47: "EHR sync scope ('what syncs') — no endpoint",
  48: "Edit payers / build from region — no endpoint",
  49: "Fee schedule detail — no endpoint",
  50: "Modifier permissions vs written rules — no endpoint",
  51: "Which NPI the coding engine keys rules on (rendering vs billing)",
  52: "Does the pipeline's 'rules' stage use the saved arrays? (else relabel)",
  53: "Per-line rule trace on the claim (which rule fired, code removed/added)",
  54: "Dry-run 'evaluate this claim against the rules' read-only call",
  55: "Are rules re-applied on note reprocess?",
  56: "CPT / ICD-10 lookup to validate a typed code",
  57: "Structured rule object (id, codes, source note, created_by/at, history)",
  58: "Role-based permission for writing rules (enforced upstream)",
  60: "Visit types API — types, per-type structure, copy-forward, panels, overrides+revert, list defaults",
  65: "CLOSED — by design: an NPI is unique within a tenant",
  66: "CLOSED — rename not allowed by design; no max length",
  67: "CLOSED — re-tag is administrative only; no re-render",
  61: "PENDING TO BE REVIEWED LATER — Visit type open items — Profile, full copy-forward list, Atteindre, copied-text marking, headings vs slots",
};

// step: { id, name, live(api-backed), optional, dq(full-only marker) }
export const GROUPS = [
  {
    id: "account",
    name: "Your Account",
    steps: [
      { id: "profile", name: "Account & profile", live: true },
      { id: "clients", name: "Clients", live: true },
      { id: "providers", name: "Providers · NPI", live: true },
      { id: "rendering", name: "Rendering providers", live: true },
      { id: "billrender", name: "Billing", live: true },
      { id: "referring", name: "Referring physicians", live: true, optional: true },
      { id: "locations", name: "Service locations", fullName: "Locations & place of service", live: true },
      { id: "ehr", name: "EHR / PM connection", live: true },
      { id: "acctype", name: "Account type & team", live: false, dq: 44 },
      { id: "practice", name: "Practice profile & Tax ID", live: false, dq: 21 },

    ],
  },
  {
    id: "connections",
    name: "Connections & Enrollment",
    steps: [
      { id: "payers", name: "Payer list", live: true },
      { id: "clearing", name: "Clearinghouse · EDI", live: false, dq: 23 },
      { id: "eraeft", name: "ERA / EFT enrollment", live: false, dq: 25 },
      { id: "fees", name: "Fee schedules", live: false, dq: 26 },
    ],
  },
  {
    id: "data",
    name: "Your Data",
    steps: [
      { id: "history", name: "Claim history import", live: false, dq: 27 },
      { id: "balances", name: "Patient balances / A·R", live: false, dq: 28 },
      { id: "records", name: "Medical records access", live: false, dq: 29 },
    ],
  },
  {
    id: "notes",
    name: "Encounter Notes",
    steps: [
      { id: "visittypes", name: "Visit types", live: true, optional: true },
      { id: "notetpl", name: "Note template", fullName: "Note templates", live: true, optional: true },
    ],
  },
  {
    id: "sched",
    name: "Scheduling (telehealth)",
    steps: [
      { id: "availability", name: "Availability", live: true, optional: true },
      { id: "intake", name: "Pre-visit intake", live: true, optional: true },
    ],
  },
  {
    id: "rules",
    name: "Billing Rules",
    steps: [
      { id: "codes", name: "Codes & modifiers", live: true, optional: true },
      { id: "coding", name: "Coding rules", live: true, optional: true },
      { id: "prefs", name: "Practice preferences", live: false, dq: 30 },
      { id: "auths", name: "Authorizations & adjustments", live: false, dq: 31 },
      { id: "mods", name: "Modifier permissions", live: false, dq: 32 },
      { id: "stmts", name: "Patient statements & self-pay", live: false, dq: 33 },
      { id: "review", name: "Review & authorize", live: false, dq: 34 },
    ],
  },
];

export function visibleGroups(mode) {
  const full = mode === "full";
  return GROUPS.map((g) => ({
    ...g,
    steps: g.steps.filter((s) => full || s.live).map((s) => (full && s.fullName ? { ...s, name: s.fullName } : s)),
  })).filter((g) => g.steps.length > 0);
}

export function flatVisibleSteps(mode) {
  return visibleGroups(mode).flatMap((g) => g.steps.map((s) => ({ ...s, group: g.name })));
}
